import React, { useState } from 'react';
import { Grid } from '@mui/material';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import {
    userWalletDefaults,
    UserWalletFormData,
    UserWalletSchema,
} from './user-wallet-from-helper';
import { TextField } from '@biq/apex-ui';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUserWallet } from '../../../../hooks';
import { useAuth, usePermission } from '../../../../core';
import { useNavigate } from 'react-router-dom';
import { WALLETS_AND_GROUPS_PATHS } from 'constants/wallets-and-groups-paths';
import { CreateFormFeatureDialog } from '../../../../components';
import {
    walletsAndGroupsCommonStyles,
    walletsAndGroupsConfig,
} from '../../wallets-and-groups-helper';

export const CreateUserWallet = (): React.JSX.Element => {
    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<UserWalletFormData>({
        mode: 'all',
        resolver: yupResolver(UserWalletSchema),
        defaultValues: userWalletDefaults,
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { getToken } = useAuth();
    const { mutate: uploadUserWallet } = useUserWallet();
    const navigate = useNavigate();
    const { hasCurrentPermissionAndRole } = usePermission();
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);

    const createUserWallet = (data: UserWalletFormData): void => {
        setIsLoading(true);
        uploadUserWallet(
            {
                token: getToken(),
                fullName: data.fullName,
                email: data.email,
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                    setTimeout(() => {
                        navigate(
                            WALLETS_AND_GROUPS_PATHS.WALLETS_AND_GROUPS_PATH
                        );
                    }, 500);
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const onSubmit = (): void => {
        const permission = hasCurrentPermissionAndRole(
            'createMetadata',
            'sdzAdmin'
        );
        if (permission) {
            handleSubmit(createUserWallet)();
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const handleCloseButton = (): void => {
        navigate(
            `${WALLETS_AND_GROUPS_PATHS.WALLETS_AND_GROUPS_PATH}?option=User Wallets`
        );
    };

    const handleBackButton = (): void => {
        navigate(
            `${WALLETS_AND_GROUPS_PATHS.CREATE_NEW_WALLETS_AND_GROUPS_PATH}?option=User Wallets`
        );
    };

    return (
        <CreateFormFeatureDialog
            buttonText='Save'
            handleSubmit={onSubmit}
            isLoading={isLoading}
            handleCloseButton={handleCloseButton}
            handleBackButton={handleBackButton}
            listConfig={walletsAndGroupsConfig}
        >
            <Grid sx={walletsAndGroupsCommonStyles.mainContainer}>
                <Grid sx={walletsAndGroupsCommonStyles.textFieldContainer}>
                    <Controller
                        name='fullName'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                label='Full Name'
                                required
                                sx={
                                    walletsAndGroupsCommonStyles.createTextFieldStyles
                                }
                                error={!!errors.fullName}
                                errorText={
                                    errors.fullName
                                        ? errors.fullName.message
                                        : ''
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid
                    mt={2}
                    sx={walletsAndGroupsCommonStyles.textFieldContainer}
                >
                    <Controller
                        name='email'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                label='Email Address'
                                required
                                sx={
                                    walletsAndGroupsCommonStyles.createTextFieldStyles
                                }
                                error={!!errors.email}
                                errorText={
                                    errors.email ? errors.email.message : ''
                                }
                            />
                        )}
                    />
                </Grid>
            </Grid>
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </CreateFormFeatureDialog>
    );
};
